import { useMemo } from 'react';

import type { ApiListingLocationType } from '#/api/types/ApiListingLocationType';
import { Tag } from '#/components/Tag/Tag';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingLocationLabel } from '#/modules/listing/components/ListingLocationLabel/ListingLocationLabel';
import { ListingLocationTypeLabel } from '#/modules/listing/components/ListingLocationTypeLabel/ListingLocationTypeLabel';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import {
  SearchHitInfoHeader,
  SearchHitInfoHeaderHeading,
  SearchHitInfoHeaderOrgName,
  SearchHitInfoHolder,
} from '#/modules/search/components/Hit/Info/SearchHitInfo.styled';
import type { DatetimeString } from '#/types/TypedDateString';
import type { EmploymentType } from '#/utils/constants/general/employmentTypes';
import {
  safeUtf8StringSlice,
  utf8StringLength,
} from '#/utils/string/safeUtf8String';

import { SearchJobHitFooter } from './Footer/SearchJobHitFooter';
import { SearchJobHitLabels } from './SearchJobHit.styled';

type Props = {
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: DatetimeString;
  } | null;
  jobTypes: EmploymentType[];
  isPostedAnonymously: boolean;
  location: string;
  locationType: ApiListingLocationType;
  name: string;
  orgName: string | null;
  orgType: string | null;
  salaryFormatted: string | null;
  showLabel?: boolean;
  published: number | null;
  now: DatetimeString;
};

export function SearchJobHitInfo({
  application,
  jobTypes,
  isPostedAnonymously,
  location,
  locationType,
  name,
  orgName,
  orgType,
  salaryFormatted,
  showLabel,
  published,
  now,
}: Props) {
  const { i18n } = useI18n();

  const jobTypeLabel = useMemo(() => {
    // Note: order is important here.
    if (jobTypes.includes('CONTRACT')) return i18n('Contract/Freelance');
    if (jobTypes.includes('FULL_TIME')) return i18n('Full Time');
    if (jobTypes.includes('PART_TIME')) return i18n('Part Time');
  }, [i18n, jobTypes]);

  return (
    <SearchHitInfoHolder>
      {showLabel && (
        <div style={{ marginBottom: 14 }}>
          <ListingTypeLabel listingType="JOB" orgType={null} />
        </div>
      )}

      <SearchHitInfoHeader>
        <SearchHitInfoHeaderHeading>
          <div>
            <span data-qa-id="search-result-link">
              {safeUtf8StringSlice(name, 0, 120)}
            </span>
            {utf8StringLength(name) > 120 && <>&hellip;</>}
          </div>

          {isPostedAnonymously && (
            <div
              style={{
                verticalAlign: 'middle',
                display: 'inline-block',
                marginLeft: '4px',
              }}
            >
              <Tooltip
                content={
                  <div style={{ padding: '6px' }}>
                    {i18n(
                      `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                    )}
                  </div>
                }
                tooltipMinimumHeight={320}
                tooltipWidth="250px"
                placement="bottom-right"
                size="small"
                variant="brand-blue"
              />
            </div>
          )}
        </SearchHitInfoHeaderHeading>

        {(orgType === 'CORPORATION' || orgName) && (
          <SearchHitInfoHeaderOrgName>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {orgName}
            </div>
          </SearchHitInfoHeaderOrgName>
        )}
      </SearchHitInfoHeader>

      <SearchJobHitLabels>
        {locationType && (
          <ListingLocationTypeLabel
            listingType="JOB"
            locationType={locationType}
          />
        )}

        {location && locationType && (
          <ListingLocationLabel
            locationType={locationType}
            location={location}
          />
        )}

        <Tag size="micro">{jobTypeLabel}</Tag>

        {salaryFormatted && <Tag size="micro">{salaryFormatted}</Tag>}
      </SearchJobHitLabels>

      <SearchJobHitFooter
        application={application}
        published={published}
        now={now}
      />
    </SearchHitInfoHolder>
  );
}
